import React, { useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Globe, LogOut } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import AutoLogout from '../components/common/AutoLogout';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import Dashboard from './Dashboard';
import GraduationDays from './GraduationDays';
import Departments from './Departments';
import Courses from './Courses';
import CourseLevels from './CourseLevels';
import ExamBodies from './ExamBodies';
import Graduates from './Graduates';
import GownRequests from './GownRequests';
import CountdownManagement from './CountdownManagement';
import Reports from './Reports';
import Users from './Users';
import AdminBooklets from './AdminBooklets';
import AdminAlumni from './AdminAlumni';
import AdminNominations from './AdminNominations';
import AdminBlog from './AdminBlog';
import AdminGallery from './AdminGallery';
import RegisteredAlumni from './RegisteredAlumni';

/** Title and context line shown in the admin header for each section. */
const PAGE_META = {
  '/admin': { title: 'Dashboard', subtitle: 'Overview of graduation activity' },
  '/admin/graduation-days': {
    title: 'Graduation Days',
    subtitle: 'Ceremony dates and venues',
  },
  '/admin/departments': { title: 'Departments', subtitle: 'Academic departments' },
  '/admin/courses': { title: 'Courses', subtitle: 'Course catalogue' },
  '/admin/course-levels': { title: 'Course Levels', subtitle: 'Levels per exam body' },
  '/admin/exam-bodies': { title: 'Exam Bodies', subtitle: 'KNEC, NITA and other bodies' },
  '/admin/graduates': { title: 'Graduates', subtitle: 'Graduation list records' },
  '/admin/gowns': { title: 'Gown Requests', subtitle: 'Collection and returns' },
  '/admin/booklets': { title: 'Booklets', subtitle: 'Graduation programme booklets' },
  '/admin/countdown': { title: 'Countdown', subtitle: 'Ceremony countdown settings' },
  '/admin/reports': { title: 'Reports', subtitle: 'Export and print records' },
  '/admin/users': { title: 'Users', subtitle: 'Staff accounts and roles' },
  '/admin/alumni': { title: 'Alumni Committee', subtitle: 'Committee members' },
  '/admin/registered-alumni': {
    title: 'Registered Alumni',
    subtitle: 'Alumni portal accounts',
  },
  '/admin/nominations': { title: 'Nominations', subtitle: 'Review alumni nominations' },
  '/admin/blog': { title: 'Blog & News', subtitle: 'Publish ceremony updates' },
  '/admin/gallery': { title: 'Gallery', subtitle: 'Homepage gallery images' },
};

const AdminDashboard = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const meta = PAGE_META[location.pathname] || {
    title: 'Admin',
    subtitle: 'Graduation management',
  };

  usePageTitle(meta.title);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <AutoLogout>
      <div className="flex h-screen overflow-hidden bg-ink-100">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={() => setIsSidebarCollapsed((collapsed) => !collapsed)}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Admin header */}
          <header className="flex h-16 flex-shrink-0 items-center justify-between gap-4 border-b border-ink-200 bg-white px-4 sm:px-6">
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold leading-tight text-ink-900 sm:text-lg">
                {meta.title}
              </h1>
              <p className="hidden truncate text-xs text-ink-500 sm:block">{meta.subtitle}</p>
            </div>

            <div className="flex flex-shrink-0 items-center gap-2">
              <Link
                to="/"
                className="hidden items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-ink-600 transition-colors hover:bg-ink-100 hover:text-brand-700 sm:inline-flex"
              >
                <Globe className="h-4 w-4" aria-hidden="true" />
                Public site
              </Link>

              <div className="flex items-center gap-2 rounded-lg border border-ink-200 py-1 pl-1 pr-2">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-700 text-xs font-bold text-white">
                  {(user?.name || 'A').charAt(0).toUpperCase()}
                </span>
                <div className="hidden min-w-0 leading-tight sm:block">
                  <p className="max-w-[9rem] truncate text-xs font-semibold text-ink-900">
                    {user?.name || 'Administrator'}
                  </p>
                  <p className="text-2xs uppercase tracking-wide text-ink-400">
                    {user?.role || 'admin'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  aria-label="Logout"
                  title="Logout"
                  className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-500 transition-colors hover:bg-red-50 hover:text-red-600"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </header>

          {/* Routed admin content */}
          <main className="flex-1 overflow-y-auto">
            <div className="p-4 pb-24 sm:p-6 sm:pb-6 lg:p-8">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/graduation-days" element={<GraduationDays />} />
                <Route path="/departments" element={<Departments />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/course-levels" element={<CourseLevels />} />
                <Route path="/exam-bodies" element={<ExamBodies />} />
                <Route path="/graduates" element={<Graduates />} />
                <Route path="/gowns" element={<GownRequests />} />
                <Route path="/countdown" element={<CountdownManagement />} />
                <Route path="/users" element={<Users />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/booklets" element={<AdminBooklets />} />
                <Route path="/alumni" element={<AdminAlumni />} />
                <Route path="registered-alumni" element={<RegisteredAlumni />} />
                <Route path="nominations" element={<AdminNominations />} />
                <Route path="/blog" element={<AdminBlog />} />
                <Route path="/gallery" element={<AdminGallery />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </AutoLogout>
  );
};

export default AdminDashboard;
